/**
 * 拓扑图验证器
 * 用于验证骨架图的拓扑结构
 */

import language from '@/utils/language'
import store from '@/store'

/**
 * 获取翻译文本
 * @param {string} key - 翻译键
 * @param {Object} params - 参数对象
 * @returns {string} 翻译后的文本
 */
function t(key, params = {}) {
  const lang = store.state.app.language
  let text = language[key][lang]
  // 替换参数
  Object.keys(params).forEach(param => {
    text = text.replace(`{${param}}`, params[param])
  })
  return text
}

/**
 * 验证完整拓扑图
 * @param {Array} nodes - 节点数组
 * @param {Array} edges - 边数组
 * @returns {Object} { valid: boolean, errors: [], warnings: [] }
 */
export function validateTopology(nodes, edges) {
  const result = {
    valid: true,
    errors: [],
    warnings: []
  };

  // 验证节点
  const nodeValidation = validateNodes(nodes);
  if (!nodeValidation.valid) {
    result.valid = false;
    result.errors.push(...nodeValidation.errors);
  }

  // 验证边
  const nodeIdSet = new Set(nodes.map(n => n.id));
  const edgeValidation = validateEdges(edges, nodeIdSet);
  if (!edgeValidation.valid) {
    result.valid = false;
    result.errors.push(...edgeValidation.errors);
  }

  // 查找重复边
  const duplicates = findDuplicateEdges(edges);
  if (duplicates.length > 0) {
    result.warnings.push(t('topoValidatorDuplicateEdges', { count: duplicates.length }));
  }

  // 查找孤立节点
  const isolated = findIsolatedNodes(nodes, edges);
  if (isolated.length > 0) {
    result.warnings.push(t('topoValidatorIsolatedNodes', { count: isolated.length, ids: isolated.join(', ') }));
  }

  return result;
}

/**
 * 验证所有节点
 * @param {Array} nodes - 节点数组
 * @returns {Object} { valid: boolean, errors: [] }
 */
export function validateNodes(nodes) {
  const errors = [];

  if (!Array.isArray(nodes)) {
    errors.push(t('topoValidatorNodesMustBeArray'));
    return { valid: false, errors };
  }

  if (nodes.length === 0) {
    errors.push(t('topoValidatorAtLeastOneNode'));
    return { valid: false, errors };
  }

  // 检查 ID 唯一性
  const idSet = new Set();
  const duplicateIds = [];

  nodes.forEach((node, index) => {
    if (!node || typeof node !== 'object') {
      errors.push(t('topoValidatorNodeMustBeObject', { index }));
      return;
    }

    if (node.id === undefined || node.id === null) {
      errors.push(t('topoValidatorNodeMissingId', { index }));
      return;
    }

    if (idSet.has(node.id)) {
      duplicateIds.push(node.id);
    } else {
      idSet.add(node.id);
    }
  });

  if (duplicateIds.length > 0) {
    errors.push(t('topoValidatorDuplicateIds', { ids: duplicateIds.join(', ') }));
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * 验证所有边
 * @param {Array} edges - 边数组
 * @param {Set} nodeIdSet - 节点 ID 集合
 * @returns {Object} { valid: boolean, errors: [] }
 */
export function validateEdges(edges, nodeIdSet) {
  const errors = [];

  if (!Array.isArray(edges)) {
    errors.push(t('topoValidatorEdgesMustBeArray'));
    return { valid: false, errors };
  }

  edges.forEach((edge, index) => {
    if (!edge || typeof edge !== 'object') {
      errors.push(t('topoValidatorEdgeMustBeObject', { index }));
      return;
    }

    const fromNodeId = edge.fromNodeId || edge.from;
    const toNodeId = edge.toNodeId || edge.to;

    if (fromNodeId === undefined || fromNodeId === null) {
      errors.push(t('topoValidatorEdgeMissingFrom', { index }));
    } else if (!nodeIdSet.has(fromNodeId)) {
      errors.push(t('topoValidatorEdgeFromNotExist', { index, id: fromNodeId }));
    }

    if (toNodeId === undefined || toNodeId === null) {
      errors.push(t('topoValidatorEdgeMissingTo', { index }));
    } else if (!nodeIdSet.has(toNodeId)) {
      errors.push(t('topoValidatorEdgeToNotExist', { index, id: toNodeId }));
    }

    if (fromNodeId === toNodeId) {
      errors.push(t('topoValidatorEdgeSelfConnect', { index }));
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * 查找重复边
 * @param {Array} edges - 边数组
 * @returns {Array} 重复边的索引数组
 */
export function findDuplicateEdges(edges) {
  const edgeSet = new Set();
  const duplicates = [];

  edges.forEach((edge, index) => {
    const fromNodeId = edge.fromNodeId || edge.from;
    const toNodeId = edge.toNodeId || edge.to;
    
    // 对于双向边，需要检查两个方向
    const key1 = `${fromNodeId}-${toNodeId}`;
    const key2 = `${toNodeId}-${fromNodeId}`;

    if (edgeSet.has(key1) || (edge.bidirectional !== false && edgeSet.has(key2))) {
      duplicates.push(index);
    } else {
      edgeSet.add(key1);
      if (edge.bidirectional !== false) {
        edgeSet.add(key2);
      }
    }
  });

  return duplicates;
}

/**
 * 查找孤立节点（没有任何连接的节点）
 * @param {Array} nodes - 节点数组
 * @param {Array} edges - 边数组
 * @returns {Array} 孤立节点的 ID 数组
 */
export function findIsolatedNodes(nodes, edges) {
  const connectedNodes = new Set();

  edges.forEach(edge => {
    const fromNodeId = edge.fromNodeId || edge.from;
    const toNodeId = edge.toNodeId || edge.to;
    connectedNodes.add(fromNodeId);
    connectedNodes.add(toNodeId);
  });

  return nodes
    .filter(node => !connectedNodes.has(node.id))
    .map(node => node.id);
}

/**
 * 检查图是否连通
 * @param {Array} nodes - 节点数组
 * @param {Array} edges - 边数组
 * @returns {boolean} 是否连通
 */
export function isGraphConnected(nodes, edges) {
  if (nodes.length === 0) return true;
  if (nodes.length === 1) return true;

  // 构建邻接表
  const adjacencyList = new Map();
  nodes.forEach(node => {
    adjacencyList.set(node.id, []);
  });

  edges.forEach(edge => {
    const fromNodeId = edge.fromNodeId || edge.from;
    const toNodeId = edge.toNodeId || edge.to;
    
    adjacencyList.get(fromNodeId).push(toNodeId);
    if (edge.bidirectional !== false) {
      adjacencyList.get(toNodeId).push(fromNodeId);
    }
  });

  // BFS 检查连通性
  const visited = new Set();
  const queue = [nodes[0].id];
  visited.add(nodes[0].id);

  while (queue.length > 0) {
    const current = queue.shift();
    const neighbors = adjacencyList.get(current) || [];

    neighbors.forEach(neighbor => {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    });
  }

  return visited.size === nodes.length;
}
